import { useCallback, useEffect, useState } from "react";
import { MessageSquare, History, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

// Commenti del consulente sul singolo campo + storico modifiche (chi, cosa, quando).
export function FieldReviewPanel({ questionCode, refreshToken }) {
  const { companyId, period, can } = useTenant();
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState(null);
  const [draft, setDraft] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const canReview = can("submission:review");
  const canWrite = can("esg:write");

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/review-comments`);
      setComments(data.filter((c) => c.question_code === questionCode));
    } catch { setComments([]); }
  }, [companyId, period?.id, questionCode]);
  useEffect(() => { load(); setShowHistory(false); setHistory(null); }, [load, refreshToken]);

  const loadHistory = async () => {
    setShowHistory((s) => !s);
    if (history) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/questionnaire/answers/${questionCode}/history`);
      setHistory(data);
    } catch { setHistory([]); }
  };
  const send = async () => {
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/review-comments`, { question_code: questionCode, text: draft.trim() });
      setDraft(""); toast.success("Commento inviato all'azienda"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resolve = async (c) => {
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/review-comments/${c.id}/resolve`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const open = comments.filter((c) => !c.resolved);
  return (
    <div className="space-y-2" data-testid="field-review-panel">
      {open.length > 0 && (
        <ul className="space-y-1" data-testid="consultant-comments">
          {open.map((c) => (
            <li key={c.id} className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm" data-testid={`review-comment-${c.id}`}>
              <MessageSquare className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" aria-hidden="true" />
              <div className="flex-1"><p className="text-amber-950">{c.text}</p>
                <p className="text-[11px] text-amber-800">{c.author_name} · {fmtDate(c.created_at)}</p></div>
              {canWrite && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => resolve(c)} data-testid={`resolve-comment-${c.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Risolto
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canReview && (
        <div className="flex gap-2 items-start">
          <Textarea rows={1} value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={2000}
            placeholder="Commento del consulente su questo campo…" className="text-sm" data-testid="review-comment-input" />
          <Button size="sm" variant="outline" disabled={!draft.trim()} onClick={send} data-testid="review-comment-send">Invia</Button>
        </div>
      )}
      <button type="button" onClick={loadHistory} className="text-xs text-slate-600 underline flex items-center gap-1" data-testid="history-toggle">
        <History className="w-3.5 h-3.5" aria-hidden="true" /> {showHistory ? "Nascondi storico" : "Storico modifiche"}
      </button>
      {showHistory && (
        <ul className="text-xs text-slate-700 space-y-1 border-l-2 border-slate-200 pl-3" data-testid="answer-history">
          {(history || []).length === 0 && <li>Nessuna modifica registrata.</li>}
          {(history || []).map((h) => (
            <li key={h.id} data-testid="history-row">
              <strong>{fmtDate(h.at)}</strong> · {h.by || "—"} · {h.action}
              {h.action !== "REVIEW_COMMENT" && <> : {fmtVal(h.old_value)} → <strong>{fmtVal(h.new_value)}</strong></>}
              {h.detail?.text && <> : “{h.detail.text}”</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
